import {
  Package,
  UserCog,
  UserCheck,
  UserPlus,
  User,
  Users,
  UsersRound,
  FileText,
  FileUser,
  Mail,
  FormInput,
  Settings,
  Workflow,
  Store,
  Video,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarCog,
  CalendarPlus,
  Building,
  Building2,
  Briefcase,
  Target,
  TrendingUp,
  DollarSign,
  Cpu,
  Heart,
  Star,
  BookOpen,
  GraduationCap,
  Construction,
  FileStack,
  FolderTree,
  MessageSquare,
  ClipboardCheck,
  ClipboardList,
  Network,
  Route,
  Link2,
  Zap,
  ExternalLink,
  Tag,
  CreditCard,
  Home,
  Kanban,
  PhoneCall,
  Handshake,
  Inbox,
  Share2,
  BookText,
  GitBranch,
  type LucideIcon,
} from 'lucide-react';

/**
 * Default icon used when no feature-specific fallback is found
 */
export const DEFAULT_MENU_ICON: LucideIcon = Package;

/**
 * Map of normalized path segments to their fallback Lucide icons
 * Used when menu items don't have features (typically first-level menus)
 *
 * Normalization rules:
 * - Lowercase
 * - Leading slash removed
 * - Trimmed
 */
export const PATH_ICON_FALLBACKS: Record<string, LucideIcon> = {
  'events': Calendar,
  'community': UsersRound,
  'marketplace': Store,
  'affiliate': GitBranch, // Vertical hierarchical structure (branches/org chart)
  'forms': ClipboardList, // Same icon as Dynamic Forms
  'settings': Settings,
  'orchestrator': Workflow,
  'knowledge': BookText,
};

/**
 * Map of normalized feature names to their fallback Lucide icons
 *
 * Normalization rules:
 * - Lowercase
 * - Spaces and underscores converted to hyphens
 * - Trimmed
 */
export const FEATURE_ICON_FALLBACKS: Record<string, LucideIcon> = {
  // Legacy mappings
  'lead-request-management': UserCog,
  'lead-request': FileText,
  'invitation': Mail,
  'forms': FormInput,
  'settings': Settings,
  'orchestrator': Workflow,
  'marketplace': Store,
  'meeting-rooms': Video,

  // Cockpit
  'cockpit': Home, // Main dashboard (home)
  'cockpit-agenda': Calendar,

  // Dashboard
  'dashboard-|-bio-individual': FileUser, // Other users' profile page
  'dashboard-|-desafio-propósito-e-legado': Target,
  'dashboard-|-desafios-2025': CalendarCheck,
  'dashboard-|-desafios-comerciais': TrendingUp,
  'dashboard-|-desafios-financeiros': DollarSign,
  'dashboard-|-desafios-tecnologia': Cpu,
  'dashboard-|-perfil-empresarial-e-de-negócios': Building2,
  'dashboard-|-perfil-pessoal-e-familiar': Users,
  'dashboard-|-perfil-profissional-e-carreira': Briefcase,
  'dashboard-|-preferências-pessoais': Heart,
  'dashboard-|-preferências-profissionais': Settings,

  // Education
  'educação-|-favoritos': Star,
  'educação-|-meus-cursos': BookOpen,
  'educação-|-vitrine-educação': GraduationCap,

  // Feedback
  'feedback-|-em-construção': Construction,

  // Forms
  'formulários-|-dinâmico': ClipboardList, // Dynamic forms

  // Hub
  'hub-|-mentores': Users,
  'hub-|-reuniões-1:1': CalendarClock, // 1:1 meetings management hub

  // JI (Individual Journey)
  'ji-|-configuração-da-agenda': CalendarCog,
  'ji-|-dados-básicos': UserCog, // User data configuration
  'ji-|-dados-da-empresa': Building,
  'ji-|-desafios-e-projetos-2025': Target,
  'ji-|-feedback-networking-experience': MessageSquare,
  'ji-|-gestão-indicações': UserPlus,
  'ji-|-self-assessment': ClipboardCheck,

  // LeadRequest
  'leadrequest-|-enduser': Video, // Participation in online meetings
  'leadrequest-|-orquestrador': Inbox, // Request/solicitation manager
  'leadrequest-|-sponspor': CalendarPlus, // Scheduling intermediation request

  // Marketplace
  'marketplace-|-mentores': Store,

  // Networking
  'networking-|-comunidade-estendida': Users,
  'networking-|-perfil-geral-convidados': UserPlus,
  'networking-|-perfil-geral-membros': Users,
  'networking-|-rede-individual-membros': Network,
  'networking-|-bio-empresarial': Building2,
  'networking-|-net-journey': Handshake, // Networking, people connecting
  'networking-|-perfil-coletivo-de-membros': Users,

  // Orchestrator
  'orquestrador-|-automações': Zap,
  'orquestrador-|-educação-|-aulas': Video,
  'orquestrador-|-educação-|-categorias': FolderTree,
  'orquestrador-|-educação-|-cursos': BookOpen,
  'orquestrador-|-evento': Calendar,
  'orquestrador-|-gestão-de-formulários': FileStack,
  'orquestrador-|-grupos-temáticos': UsersRound, // Communities/groups
  'orquestrador-|-usuários': UserCog,

  // Redirect
  'redirecionamento-|-candidattus': ExternalLink,

  // Rooms
  'salas-|-salas-clube': Video,
  'salas-|-salas-clube-10': Video,
  'salas-|-salas-clube-11': Video,
  'salas-|-salas-clube-12': Video,
  'salas-|-salas-clube-2': Video,
  'salas-|-salas-clube-3': Video,
  'salas-|-salas-clube-4': Video,
  'salas-|-salas-clube-5': Video,
  'salas-|-salas-clube-6': Video,
  'salas-|-salas-clube-7': Video,
  'salas-|-salas-clube-8': Video,
  'salas-|-salas-clube-9': Video,
  'salas-|-salas-comunidade': Video,
  'salas-|-salas-comunidade-2': Video,
  'salas-|-salas-comunidade-3': Video,
  'salas-|-salas-comunidade-4': Video,

  // Solutions
  'soluções-|-cupons': Tag,
  'soluções-|-cursos': GraduationCap,
  'soluções-|-mentoria': UserCheck,
  'soluções-|-planos': CreditCard,
  'soluções-|-serviços': Briefcase,
};

/**
 * Normalize a string for icon lookup
 * Applies lowercase and trim transformations
 * @param value - String to normalize
 * @returns Normalized string
 */
function normalizeString(value: string): string {
  return value.toLowerCase().trim();
}

/**
 * Get the fallback icon for a given feature name or path
 *
 * @param featureName - The feature name (from Feature.name)
 * @param path - The menu item path (fallback when no feature)
 * @returns The corresponding Lucide icon component or DEFAULT_MENU_ICON
 *
 * Priority:
 * 1. Feature name (if provided)
 * 2. Path (if feature not found)
 * 3. Default icon (Package)
 *
 * @example
 * ```typescript
 * // Feature match (highest priority)
 * getFeatureFallbackIcon('Cockpit') // Returns Home
 *
 * // Path match (when no feature)
 * getFeatureFallbackIcon(null, '/events') // Returns Calendar
 *
 * // Default fallback
 * getFeatureFallbackIcon(null, '/unknown') // Returns Package
 * ```
 */
export function getFeatureFallbackIcon(
  featureName?: string | null,
  path?: string | null
): LucideIcon {
  // Try feature name first
  if (featureName && featureName.trim()) {
    const normalizedKey = normalizeString(featureName).replace(/[\s_]+/g, '-');
    const foundIcon = FEATURE_ICON_FALLBACKS[normalizedKey];

    if (foundIcon) return foundIcon;
  }

  // Try path as fallback
  if (path && path.trim()) {
    const normalizedPath = normalizeString(path)
      .replace(/^\//, '') // Remove leading slash
      .split('/')[0]; // Get first segment

    const foundIcon = PATH_ICON_FALLBACKS[normalizedPath];

    if (foundIcon) return foundIcon;
  }

  return DEFAULT_MENU_ICON;
}
